import React, { useEffect, useState, useMemo } from 'react';
import api from '../../../../services/api.js';
import styles from './UserSelectModal.module.css';

export default function UserSelectModal({ route, onClose, onGrant }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorUsers, setErrorUsers] = useState(null);
  const [grantingUserId, setGrantingUserId] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      setErrorUsers(null);
      try {
        const fetched = await api.get('/users');
        setUsers(Array.isArray(fetched) ? fetched : []);
      } catch (e) {
        console.error('Ошибка загрузки пользователей:', e);
        setErrorUsers('Не удалось загрузить пользователей');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const existingIds = useMemo(
    () => (route?.permittedUsers || []).map(u => u.id),
    [route]
  );

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return users.filter(u =>
      (u.username || '').toLowerCase().includes(term) ||
      (u.firstName || '').toLowerCase().includes(term)
    );
  }, [users, search]);

  const handleUserClick = async (user) => {
    if (existingIds.includes(user.id)) {
      alert(`Пользователь ${user.username} уже имеет доступ`);
      return;
    }
    if (typeof onGrant !== 'function') {
      console.warn('onGrant not provided');
      return;
    }

    setGrantingUserId(user.id);
    try {
      await onGrant(user, route);
      // после успешной выдачи — закрываем модалку
      onClose?.();
    } catch (e) {
      console.error('Ошибка при выдаче доступа через onGrant:', e);
      alert('Не удалось выдать доступ: ' + (e.message || ''));
    } finally {
      setGrantingUserId(null);
    }
  };

  return (
    <div className={styles.userModalOverlay}>
      <div className={styles.userModal}>
        <div className={styles.userModalHeader}>
          <h3>Выдать доступ вручную</h3>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Закрыть">
            &times;
          </button>
        </div>
        <div className={styles.userModalBody}>
          <input
            type="text"
            placeholder="Фильтр по имени или логину"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={styles.userSearchInput}
          />
          {loadingUsers && <p>Загрузка пользователей...</p>}
          {errorUsers && <p className={styles.errorText}>{errorUsers}</p>}
          {!loadingUsers && !errorUsers && (
            <div className={styles.userSelectList}>
              {filtered.map(user => {
                const hasAccess = existingIds.includes(user.id);
                const isGranting = grantingUserId === user.id;
                return (
                  <div
                    key={user.id}
                    className={`${styles.userItemManual} ${
                      hasAccess ? styles.hasAccessManual : ''
                    }`}
                    onClick={() => {
                      if (hasAccess || isGranting) return;
                      handleUserClick(user);
                    }}
                    aria-disabled={hasAccess || isGranting}
                    style={{ position: 'relative' }}
                  >
                    <div>
                      <strong>{user.username}</strong>{' '}
                      <span className={styles.smallText}>{user.firstName}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {hasAccess && <div className={styles.tag}>Есть доступ</div>}
                      {!hasAccess && isGranting && (
                        <div className={styles.tag}>Выдаём...</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <p>Ничего не найдено.</p>}
            </div>
          )}
        </div>
        <div className={styles.userModalFooter}>
          <button className={styles.btnClose} onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
